import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  enterInList, htmlToMarkdown, indentList, looksLikeHtml, matchesTerms, parseTerms,
  renderMarkdown, toggleTask,
  type Edit, type MarkdownOptions,
} from '@kolibri/shared';

import { useUploads, type AttachTo } from '../../kernel/files/uploads';
import { list, useQuery } from '../../kernel/sync/store';
import { useMermaid } from './mermaid';
import { DiagramViewer } from './diagram';
import { HEADING_PREFIX, usePageBody, usePageHref } from './page-links';
import { backgroundOf } from '../../kernel/design-system/navigation';
import { useMembers, useSession } from '../../kernel/identity/session';
import { useT, type TranslationKey } from '../../kernel/i18n/i18n';
import { buttonVariants } from '../../kernel/design-system/ui/button';
import { cn } from '../../kernel/design-system/cn';
import { Avatar, Icon, useLightbox } from '../../kernel/design-system/ui';
import { Textarea } from '../../kernel/design-system/ui/field';

/**
 * What a reference in this workspace may point at.
 *
 * The renderer is handed the project keys rather than a pattern, because a
 * pattern cannot tell `WEB-42` from `UTF-8` and a line about an encoding should
 * not sprout a link to a task nobody has. Both come out of the synced cache, so
 * a reference resolves on a train like everything else here.
 *
 * `[[Onboarding]]` comes from the same cache and answers everywhere prose is
 * rendered, not only on a page: a chat message about the handbook and the
 * handbook itself should link to it the same way, and a syntax that works in
 * one box and not the next is a syntax nobody trusts.
 */
export function useMarkdownRefs(asPage = false): MarkdownOptions {
  const t = useT();
  const { workspaceId } = useSession();
  const projects = useQuery(
    () => list('project', (project) => project.workspace_id === workspaceId && !project.deleted_at),
    [workspaceId],
  );
  const pageHref = usePageHref();
  const pageBody = usePageBody();
  return useMemo(() => {
    const byKey = new Map(projects.filter((project) => project.key).map((project) => [project.key as string, project.id]));
    return {
      keys: [...byKey.keys()],
      projectHref: (key: string) => {
        const id = byKey.get(key);
        return id ? `/projects/${id}` : undefined;
      },
      pageHref,
      // A document, and a message, are not the same thing. Ids on headings and
      // `![[…]]` drawing a whole page belong to the first: a hundred messages
      // in a scroller would be a hundred `#notes` competing for one fragment,
      // and a page unrolled inside a chat bubble is not what anybody typing
      // that meant.
      ...(asPage ? { pageBody, headingPrefix: HEADING_PREFIX, embedLoopNote: t('page.embedLoop') } : {}),
    };
  }, [projects, pageHref, pageBody, asPage, t]);
}

/**
 * Follow a link to somewhere in this app without reloading it.
 *
 * Both renderers produce plain anchors — the markdown one because it emits
 * strings, the HTML one because a page's own `<a>` survives the allowlist — and
 * a plain anchor to `/t/WEB-42` is a full page load, which on an offline-first
 * app means throwing away the cache and the socket to arrive at a screen the
 * router could have drawn. Modified clicks are left alone: opening a task in a
 * new tab is a reasonable thing to want, and taking that away to be clever is
 * not.
 */
export function useInAppLinks(): (event: React.MouseEvent<HTMLElement>) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return (event) => {
    const anchor = (event.target as HTMLElement).closest?.('a');
    const href = anchor?.getAttribute('href');
    if (!anchor || !href?.startsWith('/') || anchor.target === '_blank') return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    // A task opens as a sheet over what you were reading, the way every other
    // task link in the app does.
    const background = href.startsWith('/t/') ? { state: { background: backgroundOf(location) ?? location } } : undefined;
    navigate(href, background);
  };
}

export function Markdown({ source, className = '', onChange, asPage }: {
  source?: string | null;
  className?: string;
  /**
   * This text is a page rather than a message: its headings get ids to link to,
   * and `![[Another page]]` on a line of its own draws that page here.
   */
  asPage?: boolean;
  /**
   * Given, the checkboxes can be ticked where they are rendered and this is
   * called with the rewritten markdown.
   *
   * Only where the reader owns the text. A checklist in a task description or
   * in a page is meant to be ticked off, and making somebody open an editor to
   * do it is the kind of friction that ends with the checklist going stale.
   * Somebody else's chat message is the opposite case, and stays read-only —
   * the one thing you may do to another person's words here is react to them.
   *
   * What the caller does with the rewritten markdown is the caller's business,
   * and on a page it is not a plain write: see `rewrite` in `collab.ts`, where
   * a tick becomes an edit of the CRDT rather than a replacement of it.
   */
  onChange?: (next: string) => void;
}) {
  const t = useT();
  const refs = useMarkdownRefs(asPage);
  const options = useMemo(() => ({ ...refs, interactiveTasks: !!onChange }), [refs, onChange]);
  const html = useMemo(() => renderMarkdown(source ?? '', options), [source, options]);
  /**
   * The prop object, kept alive between renders — and it has to be.
   *
   * Written inline as `dangerouslySetInnerHTML={{ __html: html }}`, this is a
   * fresh object on every render. React diffs props by reference, so a new
   * object always looks like a change, and the update it commits for that prop
   * is `element.innerHTML = html` — unconditionally, without ever comparing the
   * string. Identical markup, and every node under this div is thrown away and
   * rebuilt anyway.
   *
   * That is expensive, and worse than expensive. A DOM Selection points at text
   * nodes; replace them and the selection is gone. So selecting a passage in a
   * page — which sets state, to offer the comment button — re-rendered this
   * component, rebuilt the body underneath the reader, and dropped the very
   * selection that had just been made. The page moved at the same moment, from
   * relaying every block in it. The comment button still worked, because it had
   * already read the range: the offer survived, the selection did not.
   *
   * Diagrams paid for it too. `useMermaid` replaces a marked block with an SVG
   * after the render; a rewrite puts the unrendered source back, so every
   * render of a page with a diagram in it drew that diagram again.
   *
   * Memoised on `html`, the object stays the same one until the markup really
   * changes, React sees no change, and the body is left alone.
   */
  const inner = useMemo(() => ({ __html: html }), [html]);
  // Click-to-enlarge is delegated: the renderer produces plain HTML, so there
  // are no image components to hand a handler to.
  const { open, lightbox } = useLightbox();
  // ...and diagrams are upgraded the same way, for the same reason.
  const host = useRef<HTMLDivElement>(null);
  useMermaid(host, html, t('page.diagramOpen'));

  /*
   * Which diagram is open, held as the node itself.
   *
   * The viewer clones what is on the page rather than drawing again, so what
   * it needs is the element — and this is the only place that has it, because
   * the markup is a string React owns and there is nothing to hand a prop to.
   */
  const [opened, setOpened] = useState<SVGElement | null>(null);

  const follow = useInAppLinks();
  const click = (event: React.MouseEvent<HTMLDivElement>) => {
    const enlarge = (event.target as HTMLElement).closest?.('.md-diagram-open');
    if (enlarge) {
      event.preventDefault();
      setOpened(enlarge.parentElement?.querySelector('svg') ?? null);
      return;
    }
    open(event);
    // A checkbox does not open a lightbox and is not a link: it is answered
    // here, and the default is prevented so the box waits for the state to come
    // back rather than ticking itself first and being corrected.
    const box = (event.target as HTMLElement).closest?.('input[type=checkbox][data-task]');
    if (box && onChange) {
      event.preventDefault();
      event.stopPropagation();
      onChange(toggleTask(source ?? '', Number(box.getAttribute('data-task'))));
      return;
    }
    follow(event);
  };

  if (!source?.trim()) return null;
  return (
    <>
      <div ref={host} className={`md ${className}`} onClick={click} dangerouslySetInnerHTML={inner} />
      {lightbox}
      <DiagramViewer svg={opened} onClose={() => setOpened(null)} />
    </>
  );
}


interface EditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  autoFocus?: boolean;
  /** Hang uploaded images on this task, page, decision — whatever is being edited. */
  attachTo?: AttachTo | null;
  onSubmit?: () => void;
  /**
   * Enter submits, the way a conversation expects — Shift+Enter breaks a line
   * instead. Off by default: everywhere else in the app Enter writes a
   * paragraph, and a comment box that posts mid-thought teaches people to
   * draft elsewhere. Continuing a list still wins over submitting, so a
   * checklist can be typed into a chat without sending after every item.
   */
  submitOnEnter?: boolean;
  /**
   * The message-box shape: one line that grows with what is typed, formatting
   * folded away behind a toggle, and the buttons along the bottom instead of
   * the top.
   *
   * A conversation is mostly one-line messages, and the full editor put a
   * nine-button toolbar, a preview toggle and a three-line box in front of
   * every one of them — about a hundred and fifty pixels of furniture on a
   * screen where the messages themselves were fighting for room. What the
   * editor is *good* at underneath — pasting a screenshot, `@` and `#` — is
   * exactly right for a chat and is all still here.
   */
  compact?: boolean;
  /** Buttons for the bottom bar in `compact` mode — Send, in practice. */
  actions?: ReactNode;
  /**
   * The textarea itself, for a caller that has to touch the caret.
   *
   * The page editor does: when somebody else's paragraph arrives while you are
   * typing, the text under your cursor changes, and a caret that jumps to the
   * end of the document every time a colleague saves is not collaboration.
   */
  fieldRef?: { current: HTMLTextAreaElement | null };
}

/** One row in the `#` menu — a project or a task, offered the same way. */
interface RefChoice {
  key: string;
  /** What goes into the text: `#WEB`, or `WEB-42`. */
  token: string;
  icon: string;
  label: string;
  hint: string;
}

const SNIPPETS: { icon: string; title: TranslationKey; wrap: [string, string] }[] = [
  { icon: 'B', title: 'editor.bold', wrap: ['**', '**'] },
  { icon: 'I', title: 'editor.italic', wrap: ['_', '_'] },
  { icon: '</>', title: 'editor.code', wrap: ['`', '`'] },
  { icon: '#', title: 'editor.heading', wrap: ['## ', ''] },
  { icon: '•', title: 'editor.bulletList', wrap: ['- ', ''] },
  { icon: '☑', title: 'editor.checklist', wrap: ['- [ ] ', ''] },
  { icon: '❝', title: 'editor.quote', wrap: ['> ', ''] },
];

/**
 * What `/` at the start of a line offers.
 *
 * The one convention people arrive with from every other editor, and the reason
 * it is worth having is not novelty: the toolbar has room for seven buttons and
 * a document has more shapes than that. A table, a fenced block, a diagram and
 * a horizontal rule were all things you could only write here by remembering
 * the syntax — which meant, in practice, that people wrote paragraphs.
 *
 * `caret` is where the cursor ends up, counted from the start of what is
 * inserted; without it a table drops you after the last pipe rather than in the
 * first cell, which is the difference between a shortcut and a party trick.
 */
interface Command {
  id: string;
  title: TranslationKey;
  hint: TranslationKey;
  icon: string;
  text: string;
  caret?: number;
}

const COMMANDS: Command[] = [
  { id: 'h2', title: 'editor.cmdHeading', hint: 'editor.cmdHeadingHint', icon: 'H', text: '## ' },
  { id: 'h3', title: 'editor.cmdSubheading', hint: 'editor.cmdSubheadingHint', icon: 'h', text: '### ' },
  { id: 'ul', title: 'editor.bulletList', hint: 'editor.cmdListHint', icon: '•', text: '- ' },
  { id: 'ol', title: 'editor.cmdNumbered', hint: 'editor.cmdNumberedHint', icon: '1.', text: '1. ' },
  { id: 'todo', title: 'editor.checklist', hint: 'editor.cmdTodoHint', icon: '☑', text: '- [ ] ' },
  { id: 'quote', title: 'editor.quote', hint: 'editor.cmdQuoteHint', icon: '❝', text: '> ' },
  { id: 'code', title: 'editor.cmdCode', hint: 'editor.cmdCodeHint', icon: '</>', text: '```\n\n```\n', caret: 4 },
  { id: 'table', title: 'editor.cmdTable', hint: 'editor.cmdTableHint', icon: '▦', text: '| | |\n| --- | --- |\n| | |\n', caret: 2 },
  { id: 'divider', title: 'editor.cmdDivider', hint: 'editor.cmdDividerHint', icon: '—', text: '---\n' },
  // A whole bar rather than an empty one, so the preview shows what was picked,
  // and the caret lands behind the number — backspace and a figure is the edit.
  { id: 'bar', title: 'editor.cmdBar', hint: 'editor.cmdBarHint', icon: '▰', text: '{50%}', caret: 3 },
  { id: 'diagram', title: 'editor.cmdDiagram', hint: 'editor.cmdDiagramHint', icon: '◇', text: '```mermaid\nflowchart LR\n  A[Start] --> B[Ende]\n```\n', caret: 11 },
  // `![[` and no more: the `[[` tracker takes over from the next keystroke and
  // offers the pages, which is a better answer than a second list of titles.
  { id: 'embed', title: 'editor.cmdEmbed', hint: 'editor.cmdEmbedHint', icon: '❏', text: '![[' },
];

/**
 * Markdown editor with a live preview toggle. Images can be pasted or dropped;
 * they are downscaled in the browser first so a phone photo does not push a
 * 12 MB original through a mobile connection.
 */
export function MarkdownEditor({ value, onChange, placeholder, minHeight = 150, autoFocus, attachTo, onSubmit, submitOnEnter, compact, actions, fieldRef }: EditorProps) {
  const t = useT();
  const own = useRef<HTMLTextAreaElement>(null);
  const ref = fieldRef ?? own;
  const [preview, setPreview] = useState(false);
  /** Whether the formatting buttons are showing. Compact mode only. */
  const [tools, setTools] = useState(false);
  /** Where the caret belongs after the next render, set by `rewrite`. */
  const pending = useRef<[number, number] | null>(null);
  const [dropping, setDropping] = useState(false);
  const { busy, take } = useUploads(attachTo);
  const { workspaceId } = useSession();
  const members = useMembers();
  // The `@` menu: which handles match, and which of them is highlighted.
  const [mention, setMention] = useState<{ query: string; at: number; index: number } | null>(null);
  // ...and the `#` menu, which offers work rather than people.
  const [hash, setHash] = useState<{ query: string; at: number; index: number } | null>(null);
  // ...and the `[[` menu, which offers pages.
  const [wiki, setWiki] = useState<{ query: string; at: number; index: number } | null>(null);
  // ...and the `/` menu, which offers block shapes rather than anything named.
  const [slash, setSlash] = useState<{ query: string; at: number; index: number } | null>(null);

  /**
   * What `@` offers, read the way every other search box in the app is read.
   *
   * These four menus each had a `toLowerCase().includes()` of their own, which
   * is three things at once in a German workspace: `jorg` does not find "Jörg",
   * the words have to be adjacent and in the order they were typed, and — in
   * the `#` menu below — `WEB 12` does not find `WEB-12`, because the hyphen is
   * a character like any other to a substring.
   */
  const matches = useMemo(() => {
    if (!mention) return [];
    const terms = parseTerms(mention.query);
    return members.filter((member) => matchesTerms(`${member.name} ${member.email}`, terms)).slice(0, 6);
  }, [mention?.query, members]);

  /**
   * What `#` offers: the projects and the tasks of this workspace.
   *
   * Both, in one menu, because from the writer's side they are the same act —
   * naming the thing being discussed — and asking somebody to remember two
   * prefixes for that is a way to have neither used. Projects first: there are
   * few of them and they are what a conversation is usually about.
   *
   * Scanned from the synced cache and only while the menu is open, so a
   * workspace with ten thousand tasks costs nothing until somebody types `#`.
   */
  const refMatches = useQuery(() => {
    if (!hash) return [] as RefChoice[];
    const terms = parseTerms(hash.query);
    const projects = list('project', (project) => project.workspace_id === workspaceId && !project.archived)
      .filter((project) => matchesTerms(`${project.key} ${project.name}`, terms))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .slice(0, 3)
      .map((project): RefChoice => ({
        key: project.id,
        token: `#${project.key}`,
        icon: project.icon || '🚀',
        label: project.name ?? '',
        hint: project.key ?? '',
      }));
    const tasks = list('task', (task) => task.workspace_id === workspaceId && !task.archived && !!task.identifier)
      // `WEB-12` and `WEB 12` are the same question here, because `parseTerms`
      // reads an identifier as the two adjacent words the hyphen makes of it.
      .filter((task) => matchesTerms(`${task.identifier} ${task.title}`, terms))
      .sort((a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0))
      .slice(0, 6 - projects.length)
      .map((task): RefChoice => ({
        key: task.id,
        token: task.identifier as string,
        icon: '',
        label: task.title ?? '',
        hint: task.identifier as string,
      }));
    return [...projects, ...tasks];
  }, [hash?.query, workspaceId]);

  /**
   * What `[[` offers: the pages of this workspace, by title.
   *
   * Only pages, and only their titles, because that is what the link resolves
   * by — offering something the syntax cannot address would teach the wrong
   * thing. Nothing is offered for a title nobody has written: the link renders
   * as an invitation to write it, which is the answer, and a menu row saying
   * "create" would put the creating a step earlier than the author is.
   */
  const pageMatches = useQuery(() => {
    if (!wiki) return [] as RefChoice[];
    const terms = parseTerms(wiki.query);
    return list('page', (page) => page.workspace_id === workspaceId && !page.archived)
      .filter((page) => matchesTerms(page.title ?? '', terms))
      .sort((a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0))
      .slice(0, 6)
      .map((page): RefChoice => ({
        key: page.id,
        token: page.title ?? '',
        icon: page.icon || '📄',
        label: page.title ?? '',
        hint: page.is_template ? t('page.template') : '',
      }));
  }, [wiki?.query, workspaceId]);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  /**
   * Grow with the text, up to a point — where the browser will not do it.
   *
   * `field-sizing: content` is the whole feature in one line and the shared
   * `Textarea` already asks for it, so where it is supported this stays out of
   * the way. Measuring by hand *on top of* it was worse than doing nothing:
   * `scrollHeight` excludes the border while `box-sizing: border-box` makes
   * `height` include it, so every compact box came out two pixels short and
   * carried a scrollbar over an empty line — a grey rule down the inside edge
   * of every composer on the phone.
   *
   * The ceiling lives in the stylesheet so both paths share it: a pasted essay
   * scrolls inside the composer rather than pushing the conversation off the
   * top of the screen.
   */
  useLayoutEffect(() => {
    const field = ref.current;
    if (!compact || !field || preview) return;
    if (CSS.supports?.('field-sizing', 'content')) return;
    field.style.height = 'auto';
    const style = getComputedStyle(field);
    const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    field.style.height = `${Math.min(field.scrollHeight + borders, 240)}px`;
  }, [value, compact, preview]);

  useLayoutEffect(() => {
    if (!pending.current) return;
    const [from, to] = pending.current;
    pending.current = null;
    const field = ref.current;
    field?.focus();
    field?.setSelectionRange(from, to);
  });

  /**
   * Change the text and say where the caret ends up.
   *
   * The caret is restored in a layout effect rather than on the next animation
   * frame, and that is not tidiness: a frame is long enough to type in. Somebody
   * holding Enter down through a checklist types the next character before the
   * frame arrives, and it lands at the *old* caret — which produced lists like
   * `- [ ] wo` with the `t` somewhere at the end. A layout effect runs in the
   * same commit as the text it belongs to, so there is no gap to type into.
   */
  const rewrite = (text: string, from: number, to = from) => {
    pending.current = [from, to];
    onChange(text);
  };

  const surround = (before: string, after: string) => {
    const field = ref.current;
    if (!field) return;
    const { selectionStart: start, selectionEnd: end } = field;
    const selected = value.slice(start, end);
    rewrite(
      `${value.slice(0, start)}${before}${selected}${after}${value.slice(end)}`,
      start + before.length,
      start + before.length + selected.length,
    );
  };

  const apply = (edit: Edit) => rewrite(edit.text, edit.caret);

  const insert = (text: string) => {
    const field = ref.current;
    const at = field?.selectionStart ?? value.length;
    onChange(`${value.slice(0, at)}${text}${value.slice(at)}`);
  };

  /**
   * Notice an `@` being typed and offer the people in the workspace.
   *
   * Mentions have always resolved by first name, display name or email — the
   * writer simply had to know one. Offering them is the difference between a
   * feature people use and a feature people are told about.
   */
  const trackMention = (text: string, caret: number) => {
    const upto = text.slice(0, caret);
    // Only at a word boundary, so an email address in the middle of a
    // sentence does not open a menu.
    const found = upto.match(/(?:^|[\s(])@([\w.+-]*)$/);
    setMention(found ? { query: found[1], at: caret - found[1].length - 1, index: 0 } : null);
  };

  /** Replace the partial `@handle` with the chosen one. */
  const pickMention = (member: { name: string; email: string }) => {
    if (!mention) return;
    const field = ref.current;
    const caret = field?.selectionStart ?? value.length;
    // The handle people read back is the first name; `findMentions` accepts it.
    const handle = member.name.split(/\s+/)[0] || member.email.split('@')[0];
    setMention(null);
    rewrite(`${value.slice(0, mention.at)}@${handle} ${value.slice(caret)}`, mention.at + handle.length + 2);
  };

  /**
   * Notice a `#` being typed and offer the work it could mean.
   *
   * The same word-boundary rule as `@`: a `#` inside a word is somebody writing
   * a URL fragment or a C header, not asking for a menu. A markdown heading is
   * `# ` with a space, which cannot match here either.
   */
  const trackRef = (text: string, caret: number) => {
    const found = text.slice(0, caret).match(/(?:^|[\s(])#([\w-]*)$/);
    setHash(found ? { query: found[1], at: caret - found[1].length - 1, index: 0 } : null);
  };

  /**
   * Notice a `[[` being typed and offer the pages it could mean.
   *
   * No word-boundary rule here, unlike `@` and `#`: `[[` is two characters
   * nothing else in markdown spells, so wherever it appears it was meant. It
   * closes on a `]`, a `|` or a line break — past any of those the link is
   * already written and a menu is in the way.
   */
  const trackPage = (text: string, caret: number) => {
    const found = text.slice(0, caret).match(/\[\[([^[\]|\n]*)$/);
    setWiki(found ? { query: found[1], at: caret - found[1].length - 2, index: 0 } : null);
  };

  /**
   * Replace the half-typed `[[…` with the page's title.
   *
   * The title rather than the id, because that is what the link resolves by and
   * what the next reader of the source has to be able to understand. A `]]` the
   * author already typed is consumed rather than doubled — the editor does not
   * close the brackets for them, so anybody who closed their own would
   * otherwise end up with four.
   */
  const pickPage = (choice: RefChoice) => {
    if (!wiki) return;
    const field = ref.current;
    const caret = field?.selectionStart ?? value.length;
    const after = value.slice(caret, caret + 2) === ']]' ? caret + 2 : caret;
    setWiki(null);
    rewrite(`${value.slice(0, wiki.at)}[[${choice.token}]]${value.slice(after)}`, wiki.at + choice.token.length + 4);
  };

  /**
   * Notice a `/` at the start of a line and offer the shapes a block can take.
   *
   * Only at the start of a line, and that is the whole of the rule that keeps
   * this out of the way: a slash anywhere else is a date, a path, a fraction or
   * a URL, and a menu opening over every one of those would be a menu people
   * learn to dismiss. `/` after a list marker counts as a line start, because
   * inside a list is exactly where somebody reaches for a table.
   */
  const trackSlash = (text: string, caret: number) => {
    const found = text.slice(0, caret).match(/(?:^|\n)(?:\s*(?:[-*+]|\d+[.)])\s+)?\/([\w-]*)$/);
    setSlash(found ? { query: found[1], at: caret - found[1].length - 1, index: 0 } : null);
  };

  const commands = useMemo(() => {
    if (!slash) return [] as Command[];
    // Matched on the command's own id and on what it is called in the reader's
    // language, so `/tab` and `/tabelle` both find the table — and `/uber`
    // finds "Überschrift", which a substring over a lower-cased string could
    // not, since lowercasing an umlaut leaves it an umlaut.
    const terms = parseTerms(slash.query);
    if (!terms.length) return COMMANDS;
    return COMMANDS.filter((one) => matchesTerms(`${one.id} ${t(one.title)}`, terms));
  }, [slash?.query, t]);

  /** Take the `/query` back out and put the block in its place. */
  const pickCommand = (command: Command) => {
    if (!slash) return;
    const field = ref.current;
    const caret = field?.selectionStart ?? value.length;
    setSlash(null);
    rewrite(
      `${value.slice(0, slash.at)}${command.text}${value.slice(caret)}`,
      slash.at + (command.caret ?? command.text.length),
    );
  };

  /**
   * Replace the partial `#...` with the reference itself.
   *
   * A task goes in as its bare identifier and a project as `#KEY` — the tokens
   * people already write by hand — rather than as a markdown link. They are
   * shorter to read in a chat line, they survive being quoted and edited, and
   * the renderer turns both into links anyway. Writing a link here would make
   * the message say something different from what was typed.
   */
  const pickRef = (choice: RefChoice) => {
    if (!hash) return;
    const field = ref.current;
    const caret = field?.selectionStart ?? value.length;
    setHash(null);
    rewrite(`${value.slice(0, hash.at)}${choice.token} ${value.slice(caret)}`, hash.at + choice.token.length + 1);
  };

  /**
   * Everything that was dropped, as one insertion.
   *
   * One, and that is a fix rather than a tidy-up: this used to call `insert`
   * inside the loop, and `insert` splices into `value` — the `value` this
   * render closed over. Dropping three screenshots at once therefore spliced
   * three times into the same original string, and two of them vanished.
   */
  const upload = async (files: File[]): Promise<void> => {
    const done = await take(files);
    if (!done.length) return;
    insert(`\n${done.map((one) => `${one.image ? '!' : ''}[${one.name}](${one.url})`).join('\n')}\n`);
  };

  const snippets = SNIPPETS.map((snippet) => (
    <button
      key={snippet.title} type="button" className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }))}
      title={t(snippet.title)} aria-label={t(snippet.title)}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => surround(snippet.wrap[0], snippet.wrap[1])}
    >
      {snippet.icon}
    </button>
  ));

  const attach = (
    <label className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'cursor-pointer')} title={t('editor.attachImage')}>
      <Icon name="image" size={14} />
      <input
        type="file" hidden multiple accept="image/*,application/pdf"
        onChange={(event) => {
          void upload([...(event.target.files ?? [])]);
          event.target.value = '';
        }}
      />
    </label>
  );

  const previewToggle = (
    <button type="button" className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), preview && 'bg-active text-fg')} onClick={() => setPreview(!preview)}>
      {preview ? t('editor.write') : t('editor.preview')}
    </button>
  );

  return (
    <div className={cn('editor', dropping && 'dropping', compact && 'compact')}>
      {/* Compact keeps the same buttons and puts them underneath, where they
          are out of the way of the thing being written. The formatting row
          only exists once somebody asks for it. */}
      {compact
        ? tools && <div className="editor-toolbar">{snippets}</div>
        : (
          <div className="editor-toolbar">
            {snippets}
            {attach}
            <span className="flex-1 min-w-0" />
            {busy && <span className="text-muted text-[12.5px]">{t('editor.uploading')}</span>}
            {previewToggle}
          </div>
        )}

      {preview ? (
        <div className="md editor-preview" style={{ minHeight: compact ? 54 : minHeight }}>
          <Markdown source={value || `_${t('editor.nothingToPreview')}_`} onChange={onChange} />
        </div>
      ) : (
        <>
        <Textarea
          ref={ref}
          // Both of these have to be said as utilities, for the same reason:
          // `Textarea` ships `min-h-20` and `resize-y`, and a utility beats
          // anything `@layer components` has to say — which is why the
          // stylesheet's `resize: none` never took, and every compact box wore
          // a drag handle in the corner it could not be dragged by.
          className={compact ? 'min-h-0 resize-none' : undefined}
          style={{ minHeight: compact ? 34 : minHeight }}
          value={value}
          // The three menus are the editor's whole trick and none of them is
          // visible until somebody types the character that opens it. The
          // placeholder is the one piece of furniture that costs nothing —
          // it is only there while the box is empty, which is exactly when
          // somebody is deciding what this editor can do. A composer keeps the
          // short one: a chat box is a sentence, not a document.
          placeholder={placeholder ?? t(compact ? 'editor.placeholder' : 'editor.cmdHint')}
          onChange={(event) => {
            onChange(event.target.value);
            trackMention(event.target.value, event.target.selectionStart ?? 0);
            trackRef(event.target.value, event.target.selectionStart ?? 0);
            trackPage(event.target.value, event.target.selectionStart ?? 0);
            trackSlash(event.target.value, event.target.selectionStart ?? 0);
          }}
          onBlur={() => setTimeout(() => {
            setMention(null);
            setHash(null);
            setWiki(null);
            setSlash(null);
          }, 120)}
          onKeyDown={(event) => {
            // The block menu first of all: it opens on a character none of the
            // other three can be inside, so while it is up it owns the keys.
            if (slash && commands.length) {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const step = event.key === 'ArrowDown' ? 1 : -1;
                setSlash({ ...slash, index: (slash.index + step + commands.length) % commands.length });
                return;
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault();
                pickCommand(commands[slash.index]);
                return;
              }
              if (event.key === 'Escape') {
                setSlash(null);
                return;
              }
            }
            // Pages next: `[[#` is a page whose title starts with a hash, and
            // the `#` menu must not take the keystroke off this one.
            if (wiki && pageMatches.length) {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const step = event.key === 'ArrowDown' ? 1 : -1;
                setWiki({ ...wiki, index: (wiki.index + step + pageMatches.length) % pageMatches.length });
                return;
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault();
                pickPage(pageMatches[wiki.index]);
                return;
              }
              if (event.key === 'Escape') {
                setWiki(null);
                return;
              }
            }
            if (hash && refMatches.length) {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const step = event.key === 'ArrowDown' ? 1 : -1;
                setHash({ ...hash, index: (hash.index + step + refMatches.length) % refMatches.length });
                return;
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault();
                pickRef(refMatches[hash.index]);
                return;
              }
              if (event.key === 'Escape') {
                setHash(null);
                return;
              }
            }
            if (mention && matches.length) {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const step = event.key === 'ArrowDown' ? 1 : -1;
                setMention({ ...mention, index: (mention.index + step + matches.length) % matches.length });
                return;
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault();
                pickMention(matches[mention.index]);
                return;
              }
              if (event.key === 'Escape') {
                setMention(null);
                return;
              }
            }
            const field = ref.current;
            if ((event.metaKey || event.ctrlKey) && !event.altKey) {
              // The toolbar's three most-used buttons, where hands already are.
              const shortcut = { b: ['**', '**'], i: ['_', '_'], k: ['[', '](url)'] }[event.key.toLowerCase()];
              if (shortcut) {
                event.preventDefault();
                surround(shortcut[0], shortcut[1]);
                return;
              }
            }
            if (onSubmit && (event.metaKey || event.ctrlKey) && event.key === 'Enter') {
              event.preventDefault();
              onSubmit();
              return;
            }
            // Enter continues the list somebody is in — another bullet, the next
            // number, another empty checkbox — and ends it on an item they left
            // empty. Both are one pure rewrite of the text; see `editor.ts`.
            if (event.key === 'Enter' && !event.shiftKey && field) {
              if (field.selectionStart === field.selectionEnd) {
                const edit = enterInList(value, field.selectionStart);
                if (edit) {
                  event.preventDefault();
                  apply(edit);
                  return;
                }
              }
              // Outside a list, Enter can mean "say it" — but never while an
              // IME is composing: there Enter commits the character, and
              // submitting would post half a word.
              if (onSubmit && submitOnEnter && !event.altKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                onSubmit();
                return;
              }
            }
            if (event.key === 'Tab' && field) {
              event.preventDefault();
              // Inside a list, Tab is nesting. Everywhere else it stays an
              // indent, which is what somebody writing a code block wants.
              const edit = indentList(value, field.selectionStart, field.selectionEnd, event.shiftKey);
              if (edit) apply(edit);
              else if (!event.shiftKey) surround('  ', '');
            }
          }}
          onPaste={(event) => {
            const files = [...event.clipboardData.files];
            if (files.length) {
              event.preventDefault();
              void upload(files);
              return;
            }
            /*
             * A copy out of a browser, a mail or a word processor, as markdown.
             *
             * Every clipboard carries both flavours, and the plain-text one a
             * page hands over is the *rendering* — headings flattened, links
             * reduced to their words, a table become four lines of prose. So
             * the structure was there in the clipboard all along and this
             * editor was throwing it away, which is why pasting research into a
             * page used to mean formatting it a second time by hand.
             *
             * Only where the HTML really is a document: `looksLikeHtml` wants
             * an envelope or two different structural tags, so a copied
             * sentence that happens to carry a `<span>` still pastes as the
             * sentence. Cmd+Shift+V is unaffected — that hands over text only,
             * and there is nothing here to convert.
             */
            const html = event.clipboardData.getData('text/html');
            if (!html || !looksLikeHtml(html)) return;
            const markdown = htmlToMarkdown(html);
            if (!markdown.trim()) return;
            event.preventDefault();
            const field = ref.current;
            const start = field?.selectionStart ?? value.length;
            const end = field?.selectionEnd ?? start;
            rewrite(`${value.slice(0, start)}${markdown}${value.slice(end)}`, start + markdown.length);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDropping(true);
          }}
          onDragLeave={() => setDropping(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDropping(false);
            void upload([...event.dataTransfer.files]);
          }}
        />
        {slash && commands.length > 0 && (
          <div className="mention-menu" role="listbox" aria-label={t('editor.commands')}>
            {commands.map((command, index) => (
              <button
                key={command.id}
                type="button"
                role="option"
                aria-selected={index === slash.index}
                className={index === slash.index ? 'active' : ''}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pickCommand(command)}
              >
                <span aria-hidden="true" className="slash-icon">{command.icon}</span>
                <span className="flex-1 min-w-0 truncate">{t(command.title)}</span>
                <span className="text-muted truncate text-[11.5px]">{t(command.hint)}</span>
              </button>
            ))}
          </div>
        )}
        {wiki && pageMatches.length > 0 && (
          <div className="mention-menu" role="listbox" aria-label={t('editor.mentionPage')}>
            {pageMatches.map((choice, index) => (
              <button
                key={choice.key}
                type="button"
                role="option"
                aria-selected={index === wiki.index}
                className={index === wiki.index ? 'active' : ''}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pickPage(choice)}
              >
                <span aria-hidden="true" style={{ width: 18, textAlign: 'center' }}>{choice.icon}</span>
                <span className="flex-1 min-w-0 truncate">{choice.label}</span>
                {choice.hint && <span className="text-muted truncate text-[11.5px]">{choice.hint}</span>}
              </button>
            ))}
          </div>
        )}
        {hash && refMatches.length > 0 && (
          <div className="mention-menu" role="listbox" aria-label={t('editor.mentionWork')}>
            {refMatches.map((choice, index) => (
              <button
                key={choice.key}
                type="button"
                role="option"
                aria-selected={index === hash.index}
                className={index === hash.index ? 'active' : ''}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pickRef(choice)}
              >
                <span aria-hidden="true" style={{ width: 18, textAlign: 'center' }}>{choice.icon || '#'}</span>
                <span className="flex-1 min-w-0 truncate">{choice.label}</span>
                <span className="text-muted mono truncate text-[11.5px]">{choice.hint}</span>
              </button>
            ))}
          </div>
        )}
        {mention && matches.length > 0 && (
          <div className="mention-menu" role="listbox" aria-label={t('editor.mentionPeople')}>
            {matches.map((member, index) => (
              <button
                key={member.id}
                type="button"
                role="option"
                aria-selected={index === mention.index}
                className={index === mention.index ? 'active' : ''}
                // The menu closes on blur, and blur fires before click.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pickMention(member)}
              >
                <Avatar user={member} size={18} />
                <span className="flex-1 min-w-0 truncate">{member.name}</span>
                <span className="text-muted truncate text-[11.5px]">{member.email}</span>
              </button>
            ))}
          </div>
        )}
        </>
      )}

      {compact && (
        <div className="editor-foot">
          <button
            type="button"
            className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), tools && 'bg-active text-fg')}
            title={t('editor.formatting')}
            aria-expanded={tools}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setTools(!tools)}
          >
            Aa
          </button>
          {attach}
          {busy && <span className="text-muted text-[12px]">{t('editor.uploading')}</span>}
          <span className="flex-1 min-w-0" />
          {previewToggle}
          {actions}
        </div>
      )}
    </div>
  );
}


